import { useState } from 'react';
import { Link } from 'react-router-dom/dist';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';

import FeedFloButton from '../../atoms/FeedFloButton';
import { ArrowIcon } from '../../atoms/Icons';
import TextArea from '../../atoms/TextArea';
import LoadingSkeleton from '../../atoms/LoadingSkeleton';
import Select from '../../atoms/Select';

import './EventAccordionPanel.scss';

function EventAccordionPanel({
  className = '',
  content = null,
  left = null,
  right = null,

  expanded = false,
  linkText,
  linkTo,
  notes = '',

  onLinkClick = () => null,
  onPanelClick = () => null,
  saveComment = () => null,

  rootCause,
  rootCauses,

  loading = false,
}) {
  const [lastSavedNote, setLastSavedNote] = useState(notes);
  const [currentNote, setCurrentNote] = useState(notes);

  const [indicateSaveIsComplete, setIndicateSaveIsComplete] = useState(false);

  const [lastSavedRootCause, setLastSavedRootCause] = useState(rootCause);
  const [currentRootCause, setCurrentRootCause] = useState(rootCause);

  const onRootCauseChange = (newRootCauseId) => {
    if (newRootCauseId !== currentRootCause?.id) {
      setLastSavedRootCause(currentRootCause);
      setCurrentRootCause(rootCauses.find((cause) => cause.id === newRootCauseId));
    }
  };

  const onClickSaveNote = () => {
    const hasNoteChanged = lastSavedNote !== currentNote;
    const hasRootCauseChanged = lastSavedRootCause?.id !== currentRootCause?.id;

    if (hasRootCauseChanged || hasNoteChanged) {
      saveComment(currentRootCause?.id, currentNote);
    }

    // Update local state
    setLastSavedNote(currentNote);
    setLastSavedRootCause(currentRootCause);
    setIndicateSaveIsComplete(true);

    setTimeout(() => {
      setIndicateSaveIsComplete(false);
    }, 500);
  };

  const onClickSaveNoteDebounce = debounce(onClickSaveNote, 100);

  const onClickCancelEditingNote = () => {
    setCurrentRootCause(lastSavedRootCause);
    setCurrentNote(lastSavedNote);
  };

  const onNoteChange = (e) => {
    setCurrentNote(e.target.value);
  };

  // ctrl/cmd + Enter will save the note
  const onTextKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      // Prevent the trigger key from being added to the input
      e.preventDefault();
      onClickSaveNote();
    }
  };

  const changesDetected = lastSavedRootCause?.id !== currentRootCause?.id || lastSavedNote !== currentNote;

  if (loading) {
    return (
      <div className={`EventAccordionPanel ${className}`}>
        <div className="EventAccordionPanel-headerContent EventAccordionPanel-loading">
          <LoadingSkeleton className="EventAccordionPanel-loading--circle" />
          <div className="EventAccordionPanel-loading--centre">
            <LoadingSkeleton className="EventAccordionPanel-loading--long" />
            <LoadingSkeleton className="EventAccordionPanel-loading--short" />
            <LoadingSkeleton className="EventAccordionPanel-loading--long" />
          </div>
          <LoadingSkeleton className="EventAccordionPanel-loading--box" />
        </div>
      </div>
    );
  }

  return (
    <div className={`EventAccordionPanel ${className}`}>
      <div
        className={`EventAccordionPanel-header ${
          expanded ? 'EventAccordionPanel--panelIsOpen' : 'EventAccordionPanel--panelIsClosed'
        }`}
        onClick={onPanelClick}
      >
        <div className="EventAccordionPanel-headerContent">
          {left}
          {content}
          {right}
        </div>
        <ArrowIcon className={`EventAccordionPanel-arrow ${expanded ? 'EventAccordionPanel--openArrow' : ''}`} />
      </div>
      {expanded ? (
        <>
          <div className="EventAccordionPanel-content">
            <div className="EventAccordionPanel-rootCauseBlock">
              <Select
                className="EventAccordionPanel-rootCauseCombobox"
                label="Root Cause"
                defaultText={'Not specified'}
                itemList={rootCauses}
                onChange={(event) => onRootCauseChange(event.target.value)}
                value={currentRootCause?.id}
              />
            </div>
          </div>
          <TextArea label="Notes" value={currentNote} onChange={onNoteChange} onKeyDown={onTextKeyDown} />
          <div className="EventAccordionPanel-linksContainer">
            {linkText && (
              <Link to={linkTo} target="_blank" className="EventAccordionPanel-link" onClick={onLinkClick}>
                {linkText}
              </Link>
            )}
            <div className="EventAccordionPanel-noteButtonsContainer">
              {indicateSaveIsComplete ? null : (
                <FeedFloButton
                  className="EventAccordionPanel-cancelButton"
                  onClick={onClickCancelEditingNote}
                  disabled={!changesDetected}
                >
                  Undo
                </FeedFloButton>
              )}
              <FeedFloButton
                className="EventAccordionPanel-saveButton"
                onClick={onClickSaveNoteDebounce}
                disabled={indicateSaveIsComplete || !changesDetected}
                type={indicateSaveIsComplete ? 'secondary' : 'primary'}
              >
                {indicateSaveIsComplete ? 'Saved!' : 'Save'}
              </FeedFloButton>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

EventAccordionPanel.propTypes = {
  className: PropTypes.string,
  content: PropTypes.element,
  left: PropTypes.element,
  right: PropTypes.element,

  details: PropTypes.string,
  expanded: PropTypes.bool,
  linkText: PropTypes.string,
  linkTo: PropTypes.string,
  notes: PropTypes.string,

  onLinkClick: PropTypes.func,
  onPanelClick: PropTypes.func,
  saveComment: PropTypes.func,

  rootCause: PropTypes.object,
  rootCauses: PropTypes.array,

  loading: PropTypes.bool,
};

export default EventAccordionPanel;
